import { ApplyColumnFilter, ApplyColumnFilterProps } from '@app/components/table/ApplyColumnFilter';
import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { defineMessage, IntlFormatters, MessageDescriptor } from 'react-intl';
import { createElement } from 'react';

/** Shared table column header for material / custom ID columns. */
export const customIdColumnHeaderMessage = defineMessage({
  id: 'common.customId',
  defaultMessage: 'ID material',
});

export interface CreateCustomIdColumnOptions<T extends MRT_RowData> {
  /** Reads the material ID from the row. */
  accessor: (row: T) => string | undefined | null;
  /** Column id used for sorting and filtering. Defaults to `customId`. */
  id?: string;
  /** Override the default "ID material" header. */
  header?: string | MessageDescriptor;
  size?: number;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
  filterFn?: MRT_ColumnDef<T>['filterFn'];
  filterField?: string;
  formatFilterValue?: (value: string | undefined | null) => unknown;
}

/**
 * Builds a reusable MRT column that shows a material ID as plain text.
 * Header defaults to `common.customId` ("ID material").
 */
export function createCustomIdColumn<T extends MRT_RowData>(
  $t: IntlFormatters['$t'],
  options: CreateCustomIdColumnOptions<T>,
): MRT_ColumnDef<T> {
  const {
    accessor,
    id = 'customId',
    header,
    size = 150,
    enableSorting = true,
    enableColumnFilter,
    filterFn,
    filterField,
    formatFilterValue,
  } = options;

  const resolvedHeader =
    header == null
      ? $t(customIdColumnHeaderMessage)
      : typeof header === 'string'
        ? header
        : $t(header);

  const column: MRT_ColumnDef<T> = {
    id,
    header: resolvedHeader,
    size,
    enableSorting,
    ...(enableColumnFilter !== undefined ? { enableColumnFilter } : {}),
    ...(filterFn !== undefined ? { filterFn } : {}),
    accessorFn: (row) => accessor(row) ?? '',
  };

  if (filterField) {
    column.Cell = ({ row }) => {
      const value = accessor(row.original);
      const filterProps: ApplyColumnFilterProps<string | null | undefined> = {
        field: filterField,
        value,
        formatValue: formatFilterValue,
        children: value ?? '',
      };

      return createElement<ApplyColumnFilterProps<string | null | undefined>>(
        ApplyColumnFilter,
        filterProps,
      );
    };
  }

  return column;
}
